import { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { Link } from "react-router-dom"
import { useReactToPrint } from "react-to-print"
import { toast } from "react-toastify"
import * as XLSX from "xlsx"
import { MAP_STATUS } from "../../../constant"
import CommonSelect from "../../../core/common/commonSelect"
import Table from "../../../core/common/dataTable/index"
import TooltipOption from "../../../core/common/tooltipOption"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"
import { selectSystemsForCurrentBranch } from "../../../core/data/redux/slice/systemsSlice"
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos"
import { Class, getAllClasses } from "../../../service/classes"
import { FeeRecord, getAllFeeType, copyFeeTypes } from "../../../service/fees/index"
import { all_routes } from "../../router/all_routes"
import FeesModal from "./feesTypeModal"
import { FEE_GROUP, getAllFeeGroups } from "../../../service/fee-group"
import { SORT_OPTIONS } from "../../../core/common/selectoption/selectoption"
import { selectCurrentSession, selectSessions } from "../../../core/data/redux/slice/sessionsSlice"
import { getAllSessions } from "../../../service/sessions"
import { selectUser } from "../../../core/data/redux/slice/user"

const FeesTypes = () => {
  const systems = useSelector(selectSystemsForCurrentBranch)
  const currentBranch = useSelector(selectCurrentBranch)
  const user = useSelector(selectUser)
  const currentSession = useSelector(selectCurrentSession)
  const sessions = useSelector(selectSessions)
  const componentRef = useRef<HTMLDivElement>(null)

  // "Copy fees from previous year" modal state
  const [showCopyModal, setShowCopyModal] = useState(false)
  const [copyFromSession, setCopyFromSession] = useState("")
  const [increasePercent, setIncreasePercent] = useState<string>("")
  const [copying, setCopying] = useState(false)
  // Every campus's sessions (fetched when the modal opens) so a new campus can
  // seed its fees from another campus (e.g. Eon).
  const [allSessions, setAllSessions] = useState<any[]>([])

  useEffect(() => {
    if (!showCopyModal) return
    getAllSessions({ all: true, limit: 1000 })
      .then((r: any) => setAllSessions(r?.sessions || []))
      .catch(() => setAllSessions([]))
  }, [showCopyModal])
  const [data, setData] = useState<FeeRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedSystem, setSelectedSystem] = useState(systems[0]?._id || "")
  const [searchText, setSearchText] = useState("")
  const [editFee, setEditFee] = useState<FeeRecord | null>(null)
  const [feeGroups, setFeeGroups] = useState<FEE_GROUP[]>()
  const [classes, setClasses] = useState<Class[]>()
  const [sortBy, setSortBy] = useState(SORT_OPTIONS[0])

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  })

  const handlePrint = useReactToPrint({ contentRef: componentRef })

  const handleCopyFees = async () => {
    if (!currentSession?._id) {
      toast.error("Select the target academic year in the top bar first")
      return
    }
    if (!copyFromSession) {
      toast.error("Choose which year to copy from")
      return
    }
    setCopying(true)
    try {
      const res = await copyFeeTypes({
        fromSession: copyFromSession,
        toSession: currentSession._id,
        increasePercent: Number(increasePercent) || 0,
      })
      toast.success(res?.message || "Fees copied")
      setShowCopyModal(false)
      setCopyFromSession("")
      setIncreasePercent("")
      fetchFeeTypes()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to copy fees")
    } finally {
      setCopying(false)
    }
  }

  const fetchFeeTypes = async () => {
    setLoading(true)
    try {
      const response = await getAllFeeType({
        page: pagination.page,
        limit: pagination.limit,
        system: selectedSystem,
        session: currentSession?._id,
        searchText,
        ...sortBy.value,
      })

      const transformedData = response.data.map((feeType: FeeRecord) => ({
        ...feeType,
        classes: feeType.classes || "N/A",
        amount: feeType.amount,
        status: MAP_STATUS[feeType.status as keyof typeof MAP_STATUS],
      }))
      setData(transformedData)
      setPagination({
        ...response.pagination,
        page: response.pagination.currentPage,
      })
    } catch (error) {
      console.error("Failed to fetch fee types:", error)
      toast.error("Failed to fetch fee types")
    } finally {
      setLoading(false)
    }
  }

  const fetchFeeGroup = async () => {
    try {
      const response = await getAllFeeGroups({
        system: selectedSystem,
        branch: currentBranch?._id,
        limit: 50,
      })
      const classesResponse = await getAllClasses({
        system: selectedSystem,
        limit: 50,
      })
      setClasses(classesResponse.classes)
      setFeeGroups(response.data)
    } catch (error) {
      console.log("error:", error)
    }
  }

  useEffect(() => {
    fetchFeeTypes()
  }, [pagination.page, selectedSystem, searchText, sortBy, currentSession?._id])

  console.log("pagination.page:", pagination.page)

  useEffect(() => {
    fetchFeeGroup()
  }, [selectedSystem])

  const handleTableChange = (pagination: any) => {
    setPagination(prev => ({
      ...prev,
      page: pagination.current,
      limit: pagination.pageSize,
    }))
  }

  const handleSystemChange = (systemItem: any) => {
    setSelectedSystem(systemItem?._id || "")
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const refreshFeeType = async () => {
    await fetchFeeTypes()
  }

  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      data.map(item => ({
        ID: item._id,
        "Fee Type": item.name,
        Classes: item.classes,
        "Fees Group": item.feeGroup.name,
        Amount: item.amount,
        Status: item.status,
      }))
    )
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "FeesTypes")
    XLSX.writeFile(wb, "fees_types.xlsx")
  }

  const handleSortSelect = (item: {
    label: string
    value: {
      sortBy: string
      sortOrder: string
    }
  }) => {
    setSortBy(item)
  }

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      sorter: (a: FeeRecord, b: FeeRecord) => a.name.length - b.name.length,
    },
    {
      title: "Classes",
      dataIndex: "classes",
      render: (classes: Class[]) => (
        <div
          style={{
            display: "flex",
            gap: "5px",
          }}
        >
          {classes?.map(cls => (
            <span
              key={cls._id}
              className="badge badge-soft-success d-inline-flex align-items-center"
              style={{ display: "flex", gap: 5 }}
            >
              {cls.name}
            </span>
          ))}
        </div>
      ),
    },
    {
      title: "Fees Group",
      dataIndex: "feeGroup",
      render: (feeGroup: FeeRecord) => feeGroup?.name || "N/A",
      sorter: (a: FeeRecord, b: FeeRecord) =>
        (a.feeGroup?.name || "").length - (b.feeGroup?.name || "").length,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      sorter: (a: FeeRecord, b: FeeRecord) => a.amount - b.amount,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string) => (
        <>
          {text === "Active" ? (
            <span className="badge badge-soft-success d-inline-flex align-items-center">
              <i className="ti ti-circle-filled fs-5 me-1"></i>
              {text}
            </span>
          ) : (
            <span className="badge badge-soft-danger d-inline-flex align-items-center">
              <i className="ti ti-circle-filled fs-5 me-1"></i>
              {text}
            </span>
          )}
        </>
      ),
      sorter: (a: FeeRecord, b: FeeRecord) => a.status.length - b.status.length,
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: FeeRecord) => (
        <>
          <div className="d-flex align-items-center">
            <div className="dropdown">
              <Link
                to="#"
                className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="ti ti-dots-vertical fs-14" />
              </Link>
              <ul className="dropdown-menu dropdown-menu-right p-3">
                <li>
                  <Link
                    className={`dropdown-item rounded-1 ${
                      user.permissions?.includes("fee-type.edit") ||
                      !user.permissions?.length
                        ? ""
                        : "not-allowed"
                    }`}
                    to="#"
                    data-bs-toggle={
                      user.permissions?.includes("fee-type.edit") ||
                      !user.permissions?.length
                        ? "modal"
                        : ""
                    }
                    data-bs-target={
                      user.permissions?.includes("fee-type.edit") ||
                      !user.permissions?.length
                        ? "#edit_fees_Type"
                        : ""
                    }
                    onClick={e => {
                      if (
                        user.permissions?.includes("fee-type.edit") ||
                        !user.permissions?.length
                      ) {
                        setEditFee(record)
                      } else {
                        e.preventDefault()
                      }
                    }}
                  >
                    <i className="ti ti-edit-circle me-2" />
                    Edit
                  </Link>
                </li>
                <li>
                  <Link
                    className={`dropdown-item rounded-1 ${
                      user.permissions?.includes("fee-type.delete") ||
                      !user.permissions?.length
                        ? ""
                        : "not-allowed"
                    }`}
                    to={
                      user.permissions?.includes("fee-type.delete") ||
                      !user.permissions?.length
                        ? "#"
                        : ""
                    }
                    data-bs-toggle={
                      user.permissions?.includes("fee-type.delete") ||
                      !user.permissions?.length
                        ? "modal"
                        : undefined
                    }
                    data-bs-target={
                      user.permissions?.includes("fee-type.delete") ||
                      !user.permissions?.length
                        ? "#delete-modal"
                        : undefined
                    }
                    onClick={e => {
                      if (
                        user.permissions?.includes("fee-type.delete") ||
                        !user.permissions?.length
                      ) {
                        setEditFee(record)
                      } else {
                        e.preventDefault()
                      }
                    }}
                  >
                    <i className="ti ti-trash-x me-2" />
                    Delete
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </>
      ),
    },
  ]

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Fee Types</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Settings</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Fee Types
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption
                onRefresh={fetchFeeTypes}
                onExportPdf={handlePrint}
                onExportExcel={handleExportExcel}
              />

              <div className="mb-2 me-2">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => setShowCopyModal(true)}
                >
                  <i className="ti ti-copy me-2" />
                  Copy from Year
                </button>
              </div>
              <div className="mb-2">
                <Link
                  to="#"
                  className={`btn btn-primary ${
                    user.permissions?.includes("fee-type.create") ||
                    !user.permissions?.length
                      ? ""
                      : "not-allowed"
                  }`}
                  data-bs-toggle="modal"
                  data-bs-target="#add_fees_Type"
                >
                  <i className="ti ti-square-rounded-plus me-2" />
                  Add Fee Type
                </Link>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Fee Types List</h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="mb-3 me-2">
                  <CommonSelect
                    className="select"
                    options={systems}
                    value={systems.find(item => item._id === selectedSystem)}
                    getOptionValue={item => item._id}
                    getOptionLabel={item => item.name}
                    onChange={handleSystemChange}
                  />
                </div>
                <div className="dropdown mb-3">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-sort-ascending-2 me-2" />
                    Sort by {sortBy.label}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    {SORT_OPTIONS.map((item: any, index: any) => (
                      <li key={index}>
                        <div
                          className={`dropdown-item rounded-1 ${
                            item.label === sortBy.label ? "active" : ""
                          }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSortSelect(item)}
                        >
                          {item.label}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div
              className="card-body p-0 py-3 printable-content"
              ref={componentRef}
            >
              <div className="print-only-header">
                <div className="school-brand">
                  <img src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"} alt={campusBrandName(currentBranch)} />
                  <strong>{campusBrandName(currentBranch)}{campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}</strong>
                </div>
                <div className="report-title">Fee Types</div>
                <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
              </div>
              <Table
                dataSource={data}
                columns={columns}
                loading={loading}
                pagination={{
                  currentPage: pagination.page,
                  limit: pagination.limit,
                  total: pagination.total,
                  onChange: (page, pageSize) =>
                    handleTableChange({ current: page, pageSize }),
                }}
                setSearchText={setSearchText}
              />
            </div>
          </div>
        </div>
      </div>
      <FeesModal
        refreshList={refreshFeeType}
        editFee={editFee}
        setEditFee={setEditFee}
        selectedSystem={selectedSystem}
        feeGroups={feeGroups}
        classes={classes}
      />

      {showCopyModal && (
        <div
          className="modal fade show d-block"
          style={{ background: "rgba(0,0,0,.5)" }}
          onClick={() => !copying && setShowCopyModal(false)}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Copy Fees from Another Year</h5>
                <button type="button" className="btn-close" onClick={() => setShowCopyModal(false)} disabled={copying} />
              </div>
              <div className="modal-body">
                <p className="text-muted">
                  Copies every fee type into the current year
                  {currentSession?.title ? <strong> ({currentSession.title})</strong> : null}. Fees
                  already present are skipped.
                </p>
                <div className="mb-3">
                  <label className="form-label">Copy from (campus &amp; year) *</label>
                  <select
                    className="form-select"
                    value={copyFromSession}
                    onChange={(e) => setCopyFromSession(e.target.value)}
                  >
                    <option value="">Select a campus &amp; year</option>
                    {(allSessions.length ? allSessions : sessions || [])
                      .filter((s: any) => s._id !== currentSession?._id)
                      .map((s: any) => (
                        <option key={s._id} value={s._id}>
                          {(s.branch?.name ? `${s.branch.name} — ` : "") + s.title}
                        </option>
                      ))}
                  </select>
                  <small className="text-muted">
                    You can copy from another campus (e.g. to set up a new campus from an existing one).
                  </small>
                </div>
                <div className="mb-1">
                  <label className="form-label">Increase amounts by (%) — optional</label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="e.g. 10"
                    value={increasePercent}
                    onChange={(e) => setIncreasePercent(e.target.value)}
                  />
                  <small className="text-muted">Leave blank to copy the same amounts.</small>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={() => setShowCopyModal(false)} disabled={copying}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleCopyFees} disabled={copying}>
                  {copying ? "Copying…" : "Copy Fees"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default FeesTypes
